import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styles from './ProductCosts.module.scss';
import { IoSearch as SearchIcon } from "react-icons/io5";
import { useAppDispatch, useAppSelector } from '../../../redux/reduxHooks';
import { GoPencil as PencilIcon } from "react-icons/go";
import { MdOutlineContentCopy as CopyIcon } from "react-icons/md";
import { PaginationState, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { RestS } from '../../../rest/rest-service';
import { MetricValueType, TaxInputInf, TaxTypes } from '../../../interfaces/interfaces';
import { formatNumberWithComma, getLast12CYMonthsArr, getLocalUser, onBlurFormatNumber, parseNumberForDB } from '../../../components/functions/utilities';
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import debounce from 'lodash/debounce';
import ProductEditForm from '../ProductForm/ProductEditForm';
import ChannelFilter from '../components/channelFilter/ChannelFilter';
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import Drawer from '@mui/material/Drawer';
import SelectedProductsDrawer from '../components/SelectedProductsDrawer/SelectedProductsDrawer';
import { OverlayTrigger } from 'react-bootstrap';
import { RenderTooltip } from '../../../components/common/RenderTooltip';
import CircularLoader from '../../../components/common/CircularLoader/CircularLoader';
import { setCloseBulkModal, setRefreshFinanceStatement } from '../../../redux/financeStatement/financeStatement';
import { TbArrowNarrowDown as DownArrowIcon, TbArrowNarrowUp as UpArrowIcon } from "react-icons/tb";
import { Pagination } from '@mui/material';
import { Outlet, Route, useLocation, useNavigate, useNavigation } from 'react-router';
import ProductCostsFooter from '../components/ProductCostsFooter/ProductCostsFooter';
import { MdOutlineFileUpload as UploadIcon } from 'react-icons/md'
import BulkUpdateModal from './BulkUpdateModal/BulkUpdateModal';
import { useSearchParams } from 'react-router-dom';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { FaCircleExclamation as Exclamation } from "react-icons/fa6";
import { RiExternalLinkLine as RedirectIcon } from 'react-icons/ri'

type Props = {
    productCopyState: boolean;
    setProductCopyState: (state: boolean) => void;
    handleCancelCost: () => void;
    defaultCostMissingFilter?: boolean;
    setDefaultCostMissingFilter?: any
}
type Anchor = 'top' | 'left' | 'bottom' | 'right';

export const filterColdState = {
    searchString: "",
    integrationPartner: "All Channels",
    onlyMissingCosts: false,
    onlyMissingTaxRate: false,
    onlyProductSold: true,
    sortingState: "DESC",
    cachedProductMc: new Set<number>(),
    cachedProductTr: new Set<number>(),
    forceReload: false,
}

export const btnSpanStyle = {
    fontFamily: "Work Sans",
    fontSize: "0.6875rem",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "0.875rem",
    letterSpacing: "0.01375rem"
}

const ProductCosts = forwardRef(({ productCopyState, setProductCopyState, handleCancelCost, defaultCostMissingFilter = false, setDefaultCostMissingFilter }: Props, ref) => {
    const [data, setData] = useState([]); // Table data
    const [loading, setLoading] = useState(false); // Loading state
    const [pageData, setPageData] = useState({}); // Cached page data
    // const [pageIndex, setPageIndex] = useState(0); // Current page
    // const [pageSize, setPageSize] = useState(0); // Page size
    const [searchTerm, setSearchTerm] = useState('');
    const [apiSearchTerm, setApiSearchTerm] = useState('');
    const [paginationState, setPaginationState] = useState<PaginationState>({ pageIndex: 0, pageSize: 0 });
    const [searchCleared, setSearchCleared] = useState(false);

    const [copyProductState, setCopyProductState] = useState({ productData: null });
    const [rowSelection, setRowSelection] = useState({});
    const [oldSearchTerm, setOldSearchTerm] = useState(null);
    const [oldPageIndex, setOldPageIndex] = useState(null);
    const [oldIntegrationPartner, setOldIntegrationPartner] = useState(null);
    const navigate = useNavigate();

    const { pathname } = useLocation();
    const currentUser = getLocalUser();





    const { integrationPartners } = useAppSelector((state) => state.config);

    const { connectionStatus } = useAppSelector((state) => state.connections);

    const headerRef = useRef(null);
    const row1Ref = useRef(null);
    const row2Ref = useRef(null);
    const row3Ref = useRef(null);

    const [topForRow1, setTopForRow1] = useState(0);
    const [topForRow2, setTopForRow2] = useState(0);
    const [topForRow3, setTopForRow3] = useState(0);

    const [topRowHeights, setTopRowHeights] = useState({ row1: 0, row2: 0, row3: 0 });

    const [openForm, setOpenForm] = useState(false);
    const [formProductId, setFormProductId] = useState(null);

    // const [selectedChannel, setSelectedChannel] = useState("All Channels");
    const [state, setState] = React.useState({
        right: false,
    });


    const [searchParams, setSearchParams] = useSearchParams();
    const [allFilterState, setAllFilterState] = useState({
        ...filterColdState,
        onlyMissingCosts: !!searchParams.get('costMissing')
    })

    const [oldFilterState, setOldFilterState] = useState(filterColdState);

    const [allProducts, setAllProducts] = useState({});
    const [allProductsLoading, setAllProductsLoading] = useState(false);

    const [isAllProductSelected, setIsAllProductSelected] = useState(false);

    const [currentAllProducts, setCurrentAllProducts] = useState([]);

    const [resetLoading, setResetLoading] = useState(false);
    // const navigate

    const [cachedProductListMc, setCachedProductListMc] = useState<Set<number>>(new Set());
    const [cachedProductListTr, setCachedProductListTr] = useState<Set<number>>(new Set());

    const [taxInputDetails, setTaxInputDetails] = useState<{ taxInput: TaxInputInf, showTaxColumn: boolean }>({
        taxInput: null,
        showTaxColumn: false,
    });

    const [pageLoading, setPageLoading] = useState(true);
    const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState({
        modalState: false,
        spreadsheetData: null
    });
    const [existingSheetTracker, setExistingSheetTracker] = useState(null);
    const [showSpreadsheetWarning, setShowSpreadsheetWarning] = useState(false)
    const [showAsinColumn, setShowAsinColumn] = useState(true)


    // Change child filter as per parent
    useEffect(() => {
        if (!allFilterState.onlyMissingCosts && defaultCostMissingFilter) {
            // console.log('defaultCostMissingFilter', defaultCostMissingFilter)
            setPageData({})
            setAllFilterState({
                ...allFilterState,
                forceReload: true,
                onlyMissingCosts: defaultCostMissingFilter
            })
            table.resetPagination();
            checkIfAllToBeSelected(null, null, defaultCostMissingFilter)
        }

        return () => {
            setDefaultCostMissingFilter(false);
        }
    }, [defaultCostMissingFilter])


    // Change parent default when child's filter is changed
    useEffect(() => {
        setDefaultCostMissingFilter(allFilterState?.onlyMissingCosts)
    }, [allFilterState?.onlyMissingCosts])

    const getTaxInput = async () => {
        try {
            const { taxInput } = await RestS.fetchTaxInput();
            if (taxInput) {
                setTaxInputDetails({
                    taxInput: taxInput,
                    showTaxColumn: taxInput?.taxType == TaxTypes.variable,
                })
                return taxInput;
            } else {
                setTaxInputDetails({
                    taxInput: taxInput,
                    showTaxColumn: false,
                })
                return taxInput;
            }
        } catch (error) {
            console.log('error', error);
        }
    }

    useEffect(() => {
        (async () => {
            await getTaxInput();
            setPageLoading(false);
        })()
    }, [])




    useEffect(() => {
        if (!allFilterState.onlyMissingCosts) {
            setCachedProductListMc(new Set())
        }
    }, [allFilterState.onlyMissingCosts])

    useEffect(() => {
        if (!allFilterState.onlyMissingTaxRate) {
            setCachedProductListTr(new Set())
        }
    }, [allFilterState.onlyMissingTaxRate])









    const toggleDrawer =
        (anchor: Anchor, open: boolean) =>
            (event: React.KeyboardEvent | React.MouseEvent) => {
                // console.log('asdasd')
                if (
                    event.type === 'keydown' &&
                    ((event as React.KeyboardEvent).key === 'Tab' ||
                        (event as React.KeyboardEvent).key === 'Shift')
                ) {
                    return;
                }

                setState({ ...state, [anchor]: open });
            };

    const calculateHeights = () => {
        let totalHeight = 0;
        if (headerRef.current) {
            totalHeight += headerRef.current.offsetHeight;
            // console.log('row1 offset', headerRef.current.offsetHeight);
        }
        setTopForRow1(totalHeight);

        if (row1Ref.current) {
            totalHeight += row1Ref.current.offsetHeight;
            // console.log('row2 offset', row1Ref.current.offsetHeight);
        }
        setTopForRow2(totalHeight);

        if (row3Ref.current) {
            totalHeight += row2Ref.current.offsetHeight;
        }
        setTopForRow3(totalHeight);
    };

    useEffect(() => {
        calculateHeights();
        window.addEventListener('resize', calculateHeights);

        return () => {
            window.removeEventListener('resize', calculateHeights);
        };
    }, [productCopyState]);






    const getProductTableData = async ({ searchTerm = null, integrationPartner = allFilterState.integrationPartner, isMissingCost = allFilterState.onlyMissingCosts, pageIndex = 0, forceReload = false, sorting = allFilterState.sortingState, removeProductId, cachedProductList = null, showLoading = true, isMissingTaxRate = allFilterState.onlyMissingTaxRate, cachedProductListForTaxRate = null, isProductSold = allFilterState.onlyProductSold }) => {
        try {
            // console.log('searchTerm', searchTerm, 'pageIndex', pageIndex, 'forceReload', forceReload, 'cachedProductList', "isProductSold", isProductSold, "allFilterState", allFilterState);
            if (paginationState?.pageIndex in pageData && !forceReload) {
                // console.log('coming inside cache');
                setData(pageData[paginationState?.pageIndex])
                return;
            }
            const cachedProductMc = Array.from(cachedProductList ?? cachedProductListMc);
            const cachedProductTr = Array.from(cachedProductListForTaxRate ?? cachedProductListTr);
            // console.log('cachedProductListfor Tr', cachedProductTr)

            // console.log("cachedProduct in tr", cachedProductListTr);
            if (showLoading) setLoading(true);

            const productTableData: any = await RestS.getProductsData(
                integrationPartner == "All Channels" ? null : integrationPartner,
                searchTerm,
                pageIndex,
                sorting, true,
                isMissingCost,
                removeProductId,
                isMissingCost && cachedProductMc.length > 0 ? cachedProductMc : null,
                isMissingTaxRate,
                isMissingTaxRate && cachedProductTr.length > 0 ? cachedProductTr : null,
                isProductSold
            );
            // console.log('response', productTableData);

            const latestMonth = getLast12CYMonthsArr().shift();

            // console.log('latestMonth', latestMonth)

            const formattingProductData = productTableData?.data.map((product) => {
                const prdCost = product.inEffectValue == MetricValueType.apiCost ? product.apiCost : (product.defaultCost && product.defaultCost > 0 && product.monthYear != latestMonth) ? product?.defaultCost : product.updatedCost;  // (product.defaultCost && product.defaultCost > 0 && product.monthYear != latestMonth) - condition to check if updated_cost is from a latest month and if it is not then show the default cost otherwise show updated cost
                const pkgCost = product?.pkgDependentMetric ?
                    product?.pkgDependentMetric == "UNIT_COST" ?
                        Math.round((product?.packagingCost * prdCost) / 100) :
                        Math.round((product?.packagingCost * product?.price) / 100)
                    : product?.packagingCost
                return {
                    ...product,
                    productCost: prdCost,
                    sellingPrice: product.price,
                    packagingCost: pkgCost
                }
            })
            // console.log("formatting Products Data", formattingProductData);
            setData(formattingProductData);
            // console.log('pageSize', Math.ceil((productTableData?.paginationData.totalCount / productTableData?.paginationData.pageCount)))
            // console.log('pagination state', paginationState)
            // console.log('response', productTableData);
            if (!productTableData?.paginationData?.totalCount) {
                table.setPageCount(0)
            } else {
                const pageCount = Math.ceil((productTableData?.paginationData.totalCount / productTableData?.paginationData.pageCount));
                // console.log('pageCount', pageCount);
                table.setPageCount(pageCount);
                // table.setPageSize(pageSize ? pageSize : 1);
                // table.setPageCount(pageCount ? pageCount : 1)
            }
            // setPaginationState({
            //     ...paginationState,
            //     pageSize: Math.ceil((productTableData?.paginationData.totalCount / productTableData?.paginationData.pageCount))
            // })
            setPageData({ ...pageData, [paginationState.pageIndex]: formattingProductData })

            if (forceReload) {
                // console.log('forceReload', forceReload)
                setAllFilterState(prev => ({ ...prev, forceReload: false }))
            }
            if (showLoading) setLoading(false);
            // setLoading(false);
        } catch (error) {
            if (showLoading) setLoading(false);
            console.log('error', error);
        }

    }

    const { refreshFinanceStatement, closeBulkModal } = useAppSelector((state) => state.financeStatement);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (pathname.toLowerCase() == '/pnltracker/edit/product-costs') {
            setPageData({});
            getProductTableData({
                searchTerm: allFilterState.searchString,
                integrationPartner: allFilterState.integrationPartner,
                isMissingCost: allFilterState.onlyMissingCosts,
                pageIndex: table?.getState().pagination.pageIndex,
                forceReload: true,
                sorting: allFilterState.sortingState,
                removeProductId: productCopyState ? copyProductState?.productData?.productId : null,
                showLoading: false,
            });
        }

    }, [pathname])

    useEffect(() => {
        if (refreshFinanceStatement) {
            setPageData({});
            getProductTableData({
                searchTerm: allFilterState.searchString,
                integrationPartner: allFilterState.integrationPartner,
                isMissingCost: allFilterState.onlyMissingCosts,
                pageIndex: table?.getState().pagination.pageIndex,
                forceReload: true,
                sorting: allFilterState.sortingState,
                removeProductId: productCopyState ? copyProductState?.productData?.productId : null,
                showLoading: false,
            });
            dispatch(setRefreshFinanceStatement(false));
        }
    }, [refreshFinanceStatement])


    const ipConfigs = useMemo(() => {
        if (integrationPartners != undefined) {
            let configObj = {};
            const connectionStatusSet = new Set(connectionStatus);

            for (const ip of Object.values(integrationPartners)) {
                // console.log('ip', ip)
                const logo = ip['logo'];
                const ipId = ip["_id"]
                // if(connectionStatusSet.has(ip['_id'])) {
                configObj[ip['companyName']] = {
                    connectionStatus: connectionStatusSet.has(ipId) ? true : false,
                    logo
                };
                // } 
            }
            return configObj;
        } else {
            return {};
        }
    }, [integrationPartners, connectionStatus]);

    const [selectedProductState, setSelectedProductState] = useState({});

    const selectedRows = useMemo(() => {

        // console.log('rowSelection', rowSelection);
        const selectedProducts = Object.values(rowSelection).filter(item => item == true);

        return selectedProducts.length;

    }, [rowSelection])

    // console.log('selectedRows', selectedRows);


    const generateEmptyTds = (count: number) => {
        const tds = [];
        const tdArray = new Array(count).fill('');
        tdArray.forEach((_, i) => {
            tds.push(<td></td>)
        })
        return tds;
    }




    const changeSelectedProduct = useCallback((productData) => {
        // console.log('productData', productData)
        setSelectedProductState(prev => ({ ...prev, [productData.productId]: productData }))
        // if (isAllProductSelected) {
        //     setIsAllProductSelected(false);
        // }
    }, [isAllProductSelected]);

    const addToAllProduct = useCallback((productData) => {
        setAllProducts(prev => ({ ...prev, [productData.productId]: productData }))
    }, [allProducts, isAllProductSelected])


    useEffect(() => {
        setShowAsinColumn(allFilterState?.integrationPartner == 'All Channels' || allFilterState?.integrationPartner.includes('Amazon Vendor Central'))
    }, [allFilterState?.integrationPartner])

    const columns = useMemo(() => {
        // Define columns for React Table

        const taxRateColumn = taxInputDetails.showTaxColumn ? [
            {
                accessorKey: 'taxRate', // Accessor shows the "key" of the "data" you want to access
                header: () =>
                    <>
                        <div className={`${styles.headerTitle} ${styles.rightAlign}`}>GST (%)</div>
                    </>,
                cell: (info) => (
                    <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                        <div className={styles.rowText}>
                            {info.getValue() ? onBlurFormatNumber(info.getValue(), 2) : ''}
                        </div>
                    </div>
                ),
            }
        ] : []

        const asinColumn = showAsinColumn ? [
            {
                accessorKey: 'asin',
                header: () => <div className={styles.headerTitle}>ASIN</div>,
                cell: (info) => (
                    <div className={styles.rowContainer}>
                        <div className={styles.rowText}>
                            {info.getValue()}
                        </div>
                    </div>
                )
            }
        ] : []

        const columnStateChanges = productCopyState ? [
            {
                accessorKey: 'selectedProduct', // Accessor shows the "key" of the "data" you want to access
                header: () => <div className={styles.headerTitle}></div>, // Header, can be a string or a function for a custom component
                cell: ({ row }) => (
                    <div className={styles.checkboxTableContainer} onClick={() => {
                    }}>
                        <input checked={row.getIsSelected()} onChange={(e) => {
                            // setSelectedProductState(prev => ({ ...prev, [row.original.productId]: row.original }))
                            row.getToggleSelectedHandler()(e)
                            if (!e.target.checked) {
                                checkIfAllToBeSelected(null, null, null)
                            }
                            addToAllProduct(row.original)
                            // changeSelectedProduct(row.original)
                        }} className={styles.checkBoxInput} type='checkbox' />
                    </div>
                ),
            }
        ] : [{
            accessorKey: 'edit', // Accessor shows the "key" of the "data" you want to access
            header: () => <div className={styles.headerTitle}></div>, // Header, can be a string or a function for a custom component
            cell: (info) => (
                <div className={styles.actionItems}>
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 100, hide: 100 }}
                        overlay={(props) =>
                            RenderTooltip(
                                props,
                                'Edit Product Costs'
                            )
                        }
                    >
                        <div className={styles.iconContainer} onClick={() => {
                            setFormProductId(info?.row?.original?.productId);
                            setOpenForm(true);
                        }} >
                            <PencilIcon />
                        </div>
                    </OverlayTrigger>
                    <OverlayTrigger
                        delay={{ show: 100, hide: 100 }}
                        placement="bottom"
                        overlay={(props) =>
                            RenderTooltip(
                                props,
                                !info.row.original?.productCost ? 'Product Costs are missing' : 'Apply to More Products'
                            )
                        }
                    >
                        <div className={`${styles.iconContainer} ${!info.row.original?.productCost ? styles.disabled : ''}`} onClick={() => {

                            if (!info.row.original?.productCost) return;
                            // setCopyProductState({ productData: info.row.original })
                            // setProductCopyState(true);
                            navigate('./copy-costs/' + info.row.original.productId)

                        }}>
                            <CopyIcon />
                        </div>
                    </OverlayTrigger>
                </div>
            )
        }
        ];

        const formattedColumns = [
            ...columnStateChanges,
            {
                accessorKey: 'productName', // Accessor shows the "key" of the "data" you want to access
                header: () =>
                    <>
                        <div className={`${styles.headerTitle} ${styles.productHeader}`}>Product Name</div>
                    </>,
                cell: (info) => (
                    <div className={`${styles.rowContainer} ${styles.productContainer}`}>
                        <div className={styles.imageContainer}>
                            <img src={`${ipConfigs[info.row.original.integrationPartner]['logo']}`} alt="Data Source" />

                        </div>
                        <div className={styles.rowText}>
                            {info.getValue()}
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: 'variant',
                header: () => <div className={styles.headerTitle}>Variant</div>,
                cell: (info) => (
                    <div className={styles.rowContainer}>
                        <div className={styles.rowText}>
                            {info.getValue()}
                        </div>
                    </div>
                )
            },
            {
                accessorKey: 'sku',
                header: () => <div className={styles.headerTitle}>SKU</div>,
                cell: (info) => (
                    <div className={styles.rowContainer}>
                        <div className={styles.rowText}>
                            {info.getValue()}
                        </div>
                    </div>
                )
            },
            ...asinColumn,
            // {
            //     accessorKey: 'sellingPrice',
            //     header: () => <div className={`${styles.headerTitle} ${styles.rightAlign}`}>Selling Price</div>,
            //     cell: (info) => (
            //         <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
            //             <div className={styles.rowText}>
            //                 {info.getValue() ? formatNumberWithComma(info.getValue(), 1) : ''}
            //             </div>
            //         </div>
            //     )
            // },
            {
                accessorKey: 'productCost',
                header: () => <div className={`${styles.headerTitle} ${styles.rightAlign}`}>Unit Cost (₹)</div>,
                cell: (info) => (
                    <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                        <div className={styles.rowText}>
                            {info.getValue() ? formatNumberWithComma(info.getValue(), 1) : ''}
                        </div>
                    </div>
                )
            },
            {
                accessorKey: 'packagingCost',
                header: () => <div className={`${styles.headerTitle} ${styles.rightAlign}`}>Packaging Cost (₹)</div>,
                cell: (info) => {
                    return (
                        <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                            <div className={styles.rowText}>
                                {info.getValue() ? formatNumberWithComma(info.getValue(), 1) : ''}
                            </div>
                        </div>
                    )
                }
            },
            ...taxRateColumn,
            {
                accessorKey: 'quantity',
                header: () => <div className={`${styles.headerTitle} ${styles.sortingHeader} ${styles.rightAlign}`}>
                    <div className={styles.sortingTitle}>
                        Units Sold
                    </div>
                    <div className={styles.sortingIconContainer} onClick={() => {
                        setPageData({})
                        setAllFilterState({
                            ...allFilterState,
                            forceReload: true,
                            sortingState: allFilterState.sortingState == "ASC" ? "DESC" : "ASC"
                        });
                        table.resetPagination();
                    }}>
                        {allFilterState.sortingState == "ASC" ? <DownArrowIcon /> : <UpArrowIcon />}
                    </div>
                </div>,
                cell: (info) => (
                    <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                        <div className={styles.rowText}>
                            {info.getValue() ? formatNumberWithComma(info.getValue()) : ''}
                        </div>
                    </div>
                )
            },
        ];


        return formattedColumns;
    },
        [ipConfigs, productCopyState, isAllProductSelected, allFilterState]
    );

    // Create a table instance
    const table = useReactTable({
        data: data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
        // Define manual pagination states and functions
        manualPagination: true, // React Table v8 doesn't use usePagination anymore
        pageCount: -1, // Calculate the total page count
        state: {
            pagination: paginationState,
            rowSelection
        },
        onRowSelectionChange: setRowSelection,
        onPaginationChange: setPaginationState,
        getRowId: (row, relativeIndex, parent) => row.productId,
    });

    // Debounced search handling
    const debouncedSearch = useMemo(() => {
        return debounce((newSearchTerm) => {
            table.setPageIndex(0);
            setPageData({});

            // setApiSearchTerm(newSearchTerm);
            if (isAllProductSelected) {
                checkIfAllToBeSelected(null, newSearchTerm, null);
            }
            // console.log('currentFilterState', allFilterState);
            setAllFilterState({
                ...allFilterState,
                forceReload: true,
                searchString: newSearchTerm
            })
        }, 500);
    }, [table?.getState().pagination?.pageIndex, productCopyState, isAllProductSelected, allFilterState]);

    // Effect for handling search term changes
    useEffect(() => {
        if (apiSearchTerm) {
            debouncedSearch(apiSearchTerm);
        }
    }, [apiSearchTerm]);

    // Effect for fetching data
    useEffect(() => {
        // console.log('paginationState', paginationState)
        // console.log('triggering from use Effect');
        // console.log('allFilterState', allFilterState)
        getProductTableData(
            {
                searchTerm: allFilterState?.searchString, integrationPartner: allFilterState.integrationPartner,
                isMissingCost: allFilterState.onlyMissingCosts,
                pageIndex: paginationState.pageIndex,
                forceReload: allFilterState.forceReload,
                sorting: allFilterState.sortingState,
                isProductSold: allFilterState?.onlyProductSold,
                removeProductId: productCopyState ? copyProductState?.productData?.productId : null,
            });

    }, [allFilterState?.integrationPartner,
    allFilterState?.onlyMissingCosts,
    allFilterState?.onlyMissingTaxRate,
    allFilterState?.sortingState,
    allFilterState?.searchString,
    allFilterState?.forceReload,
    allFilterState?.onlyProductSold,
    table?.getState().pagination?.pageIndex,
        //  selectedChannel
    ]);

    // useEffect(() => {
    //     console.log('allFilter changing', allFilterState)
    // }, [allFilterState])

    useEffect(() => {
        getCurrentFilterAllProducts()
    }, [allFilterState])

    const getCurrentFilterAllProducts = async () => {
        try {
            // if (isAllProductSelected) {
            // console.log('coming inside is All products selected')
            // setAllProductsLoading(true);
            const response: any = await RestS.getProductsData(allFilterState?.integrationPartner == "All Channels" ? null : allFilterState?.integrationPartner, allFilterState?.searchString, 1, "ASC", false, allFilterState.onlyMissingCosts, productCopyState ? copyProductState?.productData?.productId : null);
            // console.log('response', response);
            if (response?.data) {
                setCurrentAllProducts(response?.data);
                // setAllProducts(response?.data);
                // const allProductsObj = response?.data.reduce((prev, curVal) => {
                //     prev['allProducts'][curVal.productId] = curVal;
                //     prev['checkedState'][curVal.productId] = isAllProductSelected;
                //     return prev;
                // }, { allProducts: {}, checkedState: {} })
                // console.log('allProductsObj', allProductsObj);
                // setAllProducts(prev => ({ ...prev, ...allProductsObj?.allProducts }));
                // table?.setRowSelection({ ...table.getState().rowSelection, ...allProductsObj?.checkedState });

            }
            // }
        } catch (error) {
            console.log('error', error)
        } finally {
            setAllProductsLoading(false);
        }
    }



    useImperativeHandle(ref, () => {
        return {
            async applyCosts() {
                try {
                    const selectedRows = table.getState().rowSelection;
                    // console.log('selected rows', selectedRows);
                    const selectedRowsIds = Object.keys(selectedRows).filter((productId) => {
                        if (selectedRows[productId]) return true;
                        else return false;
                    })
                    // console.log('selected rows ids', selectedRowsIds);
                    const apiBody = {
                        fromProduct: {
                            productId: productData.productId,
                        },
                        toProductIds: selectedRowsIds,
                        filter: {
                            selectedIntegrationPartner: null,
                            search: searchTerm,
                            onlyMissingCosts: false
                        },
                        isAllSelected: false
                    }
                    // console.log('apiBody', apiBody);
                    const data = await RestS.copyProductCosts(apiBody);
                    if (data) {
                        setProductCopyState(false);

                        setIsAllProductSelected(false);
                        table.resetRowSelection();
                        setPageData({});
                        // console.log('allFilterState', oldFilterState)
                        // console.log('triggering from cancel copy cost');
                        setCachedProductListMc(oldFilterState?.cachedProductMc);
                        setApiSearchTerm(oldFilterState?.searchString);

                        setAllFilterState({ ...oldFilterState, forceReload: true });
                        table.setPageIndex(0);
                        setCopyProductState({ productData: null })
                        setCopyProductState({ productData: null })
                    }
                } catch (error) {
                    console.log('error', error);
                }

            },
            cancelCopyCost() {
                try {

                    // console.log('running cancelling product costs');
                    setProductCopyState(false);

                    setIsAllProductSelected(false);
                    table.resetRowSelection();
                    setPageData({});
                    // console.log('allFilterState', oldFilterState)


                    // setTimeout(() => {
                    // console.log('triggering from cancel copy cost');
                    setCachedProductListMc(oldFilterState?.cachedProductMc);
                    setApiSearchTerm(oldFilterState?.searchString);

                    setAllFilterState({ ...oldFilterState, forceReload: true });
                    table.setPageIndex(0);
                    setCopyProductState({ productData: null })
                } catch (error) {
                    console.log('error', error);
                }
            },
            getButtonState() {
                return selectedRows;
            }
        }
    }, [productCopyState, oldFilterState, table?.getState().rowSelection, selectedRows]);




    // useEffect(() => {
    //     if (productCopyState) {
    //         // console.log('searchTerm', searchTerm);
    //         setOldSearchTerm(searchTerm);
    //         setPageData({});
    //         setOldFilterState({ ...allFilterState, cachedProductMc: allFilterState?.onlyMissingCosts ? new Set(cachedProductListMc) : new Set() })
    //         setAllFilterState({ ...filterColdState, forceReload: true })
    //         setSearchCleared(true);
    //         setApiSearchTerm('');
    //         setOldPageIndex(table?.getState().pagination?.pageIndex);
    //         getProductTableData({
    //             searchTerm: '', integrationPartner: filterColdState.integrationPartner, isMissingCost: filterColdState.onlyMissingCosts, pageIndex: 0, forceReload: true,
    //             sorting: filterColdState.sortingState,
    //             removeProductId: productCopyState ? copyProductState?.productData?.productId : null
    //         });
    //     }
    // }, [productCopyState])


    const monthLabels = useMemo(() => {
        return getLast12CYMonthsArr();
    }, [])


    const closeForm = (forceLoadTable = false) => {
        setOpenForm(false);
        let updatedProductId = formProductId;
        setFormProductId(null);
        setPageData({});
        if (forceLoadTable) {
            let oldCachedProductListMc = null;
            if (allFilterState.onlyMissingCosts) {
                oldCachedProductListMc = new Set(cachedProductListMc);
                oldCachedProductListMc.add(updatedProductId);
                setCachedProductListMc(oldCachedProductListMc);
            }
            let oldCachedProductListTr = null;
            if (allFilterState.onlyMissingTaxRate) {
                oldCachedProductListTr = new Set(cachedProductListTr);
                oldCachedProductListTr.add(updatedProductId);
                setCachedProductListTr(oldCachedProductListTr);
            }
            // console.log('oldCachedProductListTr', oldCachedProductListTr)
            getProductTableData({ searchTerm: apiSearchTerm, integrationPartner: allFilterState.integrationPartner, isMissingCost: allFilterState.onlyMissingCosts, pageIndex: table?.getState().pagination?.pageIndex, forceReload: true, sorting: allFilterState.sortingState, removeProductId: null, cachedProductList: oldCachedProductListMc, isMissingTaxRate: allFilterState.onlyMissingTaxRate, cachedProductListForTaxRate: oldCachedProductListTr });
        }
    }





    const { productData } = copyProductState;

    const changeSelectedChannel = async (value) => {
        // console.log('value of channel changed', value);
        setPageData({});
        // setSelectedChannel(value)
        setAllFilterState({
            ...allFilterState,
            forceReload: true,
            integrationPartner: value
        })
        table.setPageIndex(0);
        // console.log('allState', table?.getIsAllPageRowsSelected());
        let initialAllSelected = false;
        if (isAllProductSelected) {
            console.log('allPage row was select', table?.getIsAllPageRowsSelected())
            // table.resetRowSelection();
            initialAllSelected = true;
            checkIfAllToBeSelected(value, null, null);
        }
        const data = await getProductTableData({
            searchTerm: allFilterState.searchString,
            integrationPartner: value,
            isMissingCost: allFilterState.onlyMissingCosts,
            pageIndex: 0,
            forceReload: true,
            sorting: allFilterState.sortingState,
            isProductSold: allFilterState?.onlyProductSold,
            removeProductId: productCopyState ? copyProductState?.productData?.productId : null
        });
    }

    const checkIfAllToBeSelected = async (integrationPartner = allFilterState.integrationPartner, searchString = allFilterState.searchString, missingCosts = allFilterState.onlyMissingCosts, missingTaxRate = allFilterState.onlyMissingTaxRate, onlyProductSold = allFilterState.onlyProductSold) => {
        try {
            // console.log('coming inside checkIf all to be selected', allFilterState)
            const response: any = await RestS.getProductsData(integrationPartner == "All Channels" ? null : integrationPartner, searchString, 1, "ASC", false, missingCosts, null, null, missingTaxRate, null, onlyProductSold);
            if (response?.data) {
                for (const product of response?.data) {
                    if (!rowSelection[product.productId]) {
                        // console.log('checkIfAllToBeSelected', false)
                        setIsAllProductSelected(false);
                        break;
                    }
                }
            }
        } catch (error) {
            console.log('error', error);
        }
    }

    const getAllProducts = async () => {
        try {
            if (isAllProductSelected) {
                // console.log('coming inside is All products selected')
                setAllProductsLoading(true);
                // const response: any = await RestS.getProductsData(allFilterState?.integrationPartner == "All Channels" ? null : allFilterState?.integrationPartner, allFilterState?.searchString, 1, "ASC", false, allFilterState.onlyMissingCosts);
                // console.log('response', response);
                if (currentAllProducts) {
                    // setAllProducts(response?.data);
                    const allProductsObj = currentAllProducts.reduce((prev, curVal) => {
                        prev['allProducts'][curVal.productId] = curVal;
                        prev['checkedState'][curVal.productId] = isAllProductSelected;
                        return prev;
                    }, { allProducts: {}, checkedState: {} })

                    // console.log('original Row Selection', table?.getState().rowSelection);
                    // console.log('checkedState', allProductsObj?.checkedState)
                    // console.log('allProductsObj', allProductsObj);
                    setAllProducts(prev => ({ ...prev, ...allProductsObj?.allProducts }));
                    table?.setRowSelection({ ...table.getState().rowSelection, ...allProductsObj?.checkedState });
                }
            }
        } catch (error) {
            console.log('error', error)
        } finally {
            setAllProductsLoading(false);
        }
    }

    useEffect(() => {
        // console.log('triggering all Products api call')
        getAllProducts();
    }, [isAllProductSelected]);

    const deselectAllProducts = async () => {
        try {
            // const currentRowSelection = table?.getState().rowSelection;
            // const response: any = await RestS.getProductsData(allFilterState?.integrationPartner == "All Channels" ? null : allFilterState?.integrationPartner, allFilterState?.searchString, 1, "ASC", false, allFilterState.onlyMissingCosts);
            if (currentAllProducts) {
                const manipulatedRowSelection = currentAllProducts.reduce((prev, curVal) => {
                    prev[curVal.productId] = false;
                    return prev;
                }, {})
                table?.setRowSelection({ ...table.getState().rowSelection, ...manipulatedRowSelection });
            }
        } catch (error) {
            console.log('error', error)
        }
    }


    const getCanNextPage = useCallback(() => {
        // console.log('isPage Available', !(table?.getState().pagination.pageIndex + 1 < table?.getPageCount()))
        // console.log('table state', table.getState());
        // console.log('pageIndex', table?.getState().pagination.pageIndex + 1, 'pageCount', table?.getPageCount())
        return !((table?.getState().pagination.pageIndex + 1) < table?.getState()['pagination']['pageCount'])
    }, [table?.getState().pagination]);

    const handleBulkUpdate = async () => {
        try {
            const existingTracker = await RestS.checkExistingProductSheetTracker();
            if (existingTracker?.spreadsheetTracker) {
                // console.log('existingTracker', existingTracker);
                setExistingSheetTracker(existingTracker?.spreadsheetTracker);
                setShowSpreadsheetWarning(true);
            } else {
                setIsBulkUpdateOpen({
                    modalState: true,
                    spreadsheetData: null,
                })
                setExistingSheetTracker(null);
            }
        } catch (error) {
            console.log('error', error);
        }
    }

    const [continueUpdatingLoading, setContinueUpdatingLoading] = useState(false);

    const continueSheetUpdating = async () => {
        try {
            setContinueUpdatingLoading(true);
            const sheetAccessChanged: any = await RestS.continueSheetUpdating();
            setIsBulkUpdateOpen({
                modalState: true,
                spreadsheetData: sheetAccessChanged?.updatedTracker,
            })
            setShowSpreadsheetWarning(false);
            setContinueUpdatingLoading(false)
        } catch (error) {
            setContinueUpdatingLoading(false);
            setShowSpreadsheetWarning(false);
            console.log('error', error);
        }
    }

    useEffect(() => {
        if (closeBulkModal) {
            // console.log('coming close bulk modal')
            setIsBulkUpdateOpen({
                modalState: false,
                spreadsheetData: null
            })
            dispatch(setCloseBulkModal(false))
        }
    }, [closeBulkModal])

    return (
        <>
            {pathname.includes("copy-costs") ? null : <div className={styles.productCostsRootContainer}>
                {pageLoading ? <></> :
                    <>
                        <div className={styles.topContainer}>
                            <div className={styles.leftContainer}>
                                <div className={styles.fieldContainer}>
                                    <div className={styles.labelText}>
                                        Sales Channel
                                    </div>
                                    <ChannelFilter ipConfig={ipConfigs} selectedValue={allFilterState?.integrationPartner} changeSelectedValue={changeSelectedChannel} />
                                </div>
                                <div className={styles.fieldContainer}>
                                    {/* <div className={styles.labelText}>
                            Sales Channel 
                        </div> */}

                                    <div className={styles.labelText}>
                                        Search for Products
                                    </div>

                                    <div className={styles.searchRootContainer}>
                                        <div className={styles.searchIcon}>
                                            <SearchIcon />
                                        </div>

                                        <input placeholder='Product Name, Variant, SKU' className={styles.searchInput} type="text" value={apiSearchTerm} onChange={(e) => {

                                            // console.log('searchValue', searchedValue)
                                            if (e.target.value) {
                                                // setSearchTerm(e.target.value);
                                                setApiSearchTerm(e.target.value);
                                                // setAllFilterState({
                                                //     ...allFilterState,
                                                //     searchString: e.target.value
                                                // })
                                            } else {
                                                // console.log('running this');
                                                setSearchCleared(true);
                                                setPageData({});
                                                setAllFilterState({
                                                    ...allFilterState,
                                                    forceReload: true,
                                                    searchString: ''
                                                })
                                                setApiSearchTerm('');
                                                // setSearchTerm('');
                                                table.setPageIndex(0);
                                                checkIfAllToBeSelected(null, '', null)
                                            }
                                            // if (productCopyState) {
                                            //     table?.resetRowSelection();
                                            // }
                                        }} />
                                    </div>


                                </div>

                                <div className={styles.checkboxRootContainer}>
                                    <div className={styles.checkboxLabelText}>
                                        Filter Products
                                    </div>
                                    <div className={styles.checkboxFieldContainer}>
                                        <div className={styles.checkboxContainer}>
                                            <div className={styles.checkbox}>
                                                <input checked={allFilterState.onlyMissingCosts} onChange={(e) => {
                                                    setPageData({})
                                                    setAllFilterState({
                                                        ...allFilterState,
                                                        forceReload: true,
                                                        onlyMissingCosts: e.target.checked
                                                    })
                                                    table.resetPagination();
                                                    checkIfAllToBeSelected(null, null, e.target.checked)
                                                }} type="checkbox" />
                                            </div>
                                            <div className={styles.checkboxLabel}>
                                                Missing Unit Cost
                                            </div>
                                        </div>

                                        {taxInputDetails?.showTaxColumn ? <div className={styles.checkboxContainer}>
                                            <div className={styles.checkbox}>
                                                <input checked={allFilterState.onlyMissingTaxRate} onChange={(e) => {
                                                    setPageData({})
                                                    setAllFilterState({
                                                        ...allFilterState,
                                                        forceReload: true,
                                                        onlyMissingTaxRate: e.target.checked
                                                    })
                                                    table.resetPagination();
                                                    checkIfAllToBeSelected(null, null, null, e.target.checked)
                                                }} type="checkbox" />
                                            </div>
                                            <div className={styles.checkboxLabel}>
                                                Missing GST
                                            </div>
                                        </div> : null}

                                        <div className={styles.checkboxContainer}>
                                            <div className={styles.checkbox}>
                                                <input checked={allFilterState.onlyProductSold} onChange={(e) => {
                                                    setPageData({})
                                                    setAllFilterState({
                                                        ...allFilterState,
                                                        forceReload: true,
                                                        onlyProductSold: e.target.checked
                                                    })
                                                    table.resetPagination();
                                                    checkIfAllToBeSelected(null, null, null, e.target.checked)
                                                }} type="checkbox" />
                                            </div>
                                            <div className={styles.checkboxLabel}>
                                                Sold in the Last 12 Months
                                            </div>
                                        </div>
                                    </div>


                                </div>
                            </div>

                            <div className={styles.rightContainer}>
                                <div className={styles.btnContainer}>
                                    <MeritoButton size='md' onClick={handleBulkUpdate} icon={<UploadIcon />} type='secondary-2' btnText={"Bulk Update"} />
                                </div>
                            </div>
                        </div>



                        <div className={styles.productTableContainer}>
                            <table>
                                <thead>
                                    {table.getHeaderGroups().map((headerGroup, groupIndex) => {
                                        return <tr ref={headerRef} key={headerGroup.id}>
                                            {headerGroup.headers.map((header, headerIndex) => {
                                                return (
                                                    <>
                                                        <th>
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                        </th>
                                                    </>
                                                )
                                            })}
                                        </tr>
                                    })}
                                </thead>

                                <tbody>
                                    {productCopyState ?
                                        <>
                                            <tr ref={row1Ref} className={styles.copyCostsHeader} style={{ top: `${topForRow1}px` }}>
                                                <td></td>
                                                <td>Copy Costs From</td>
                                                {generateEmptyTds(5)}
                                            </tr>
                                            <tr ref={row2Ref} className={styles.copyCostsProduct} style={{ top: `${topForRow2}px` }}>
                                                <td></td>
                                                <td>
                                                    <div className={`${styles.rowContainer} ${styles.productContainer}`}>
                                                        <div className={styles.imageContainer}>
                                                            <img src={`${ipConfigs[productData?.integrationPartner]['logo']}`} alt="Data Source" />

                                                        </div>
                                                        <div className={styles.rowText}>
                                                            {productData?.productName}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`${styles.rowContainer} `}>
                                                        <div className={styles.rowText}>
                                                            {productData?.variant ? formatNumberWithComma(productData?.variant) : ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`${styles.rowContainer} `}>
                                                        <div className={styles.rowText}>
                                                            {productData?.sku ? productData?.sku : ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`${styles.rowContainer} `}>
                                                        <div className={styles.rowText}>
                                                            {productData?.asin ? productData?.asin : ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`${styles.rowContainer} `}>
                                                        <div className={styles.rowText}>
                                                            {productData?.asin ? productData?.asin : ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                                                        <div className={styles.rowText}>
                                                            {productData?.productCost ? formatNumberWithComma(productData?.productCost) : ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                                                        <div className={styles.rowText}>
                                                            {productData?.taxRate ? productData?.taxRate : ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                {/* <td>
                                                    <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                                                        <div className={styles.rowText}>
                                                            {productData?.quantity ? formatNumberWithComma(productData?.quantity) : ''}
                                                        </div>
                                                    </div>
                                                </td> */}
                                                <td>
                                                    <div className={`${styles.rowContainer} ${styles.rightAlign}`}>
                                                        <div className={styles.rowText}>
                                                            {productData?.quantity ? formatNumberWithComma(productData?.quantity) : ''}
                                                        </div>
                                                    </div>
                                                </td>

                                            </tr>
                                            <tr ref={row3Ref} className={styles.copyCostsHeader} style={{ top: `${topForRow3}px` }}>
                                                <td>
                                                    <div className={styles.checkboxTableContainer} onClick={() => {
                                                    }}>
                                                        <input
                                                            checked={isAllProductSelected}
                                                            onChange={(e) => {
                                                                setIsAllProductSelected(prev => !prev);
                                                                if (!e.target.checked) {
                                                                    deselectAllProducts();
                                                                }
                                                            }}
                                                            // checked={table.getIsAllRowsSelected()}
                                                            //  onChange={table.getToggleAllRowsSelectedHandler()}
                                                            className={styles.checkBoxInput} type='checkbox' />
                                                    </div>
                                                </td>
                                                <td>To</td>
                                                {generateEmptyTds(5)}
                                            </tr>
                                        </>

                                        : null}
                                    {loading ? null : table.getRowModel().rows.map(row => {
                                        return (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map(cell => {
                                                    return (
                                                        <td key={cell.id}>
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            {loading ?

                                <div className={styles.loaderContainer}>
                                    <CircularLoader />
                                </div> : !table.getRowModel().rows.length ?
                                    <div className={styles.coldStateContainer}>
                                        <div className={styles.coldStateText}>
                                            No Products match your search criteria.
                                        </div>
                                    </div> : null}

                        </div>

                        <ProductCostsFooter table={table} />

                        {openForm ? <ProductEditForm showTaxColumn={taxInputDetails?.showTaxColumn} ipConfigs={ipConfigs} monthLabels={monthLabels} closeForm={closeForm} productId={formProductId} isOpen={openForm} setOpen={setOpenForm} /> : null}

                        <BulkUpdateModal bulkUpdateState={isBulkUpdateOpen} handleClose={(closeStatus) => setIsBulkUpdateOpen(closeStatus)} productsFilter={{
                            isMissingCosts: allFilterState.onlyMissingCosts,
                            isMissingTaxRate: allFilterState.onlyMissingTaxRate,
                            isProductSold: allFilterState.onlyProductSold,
                            integrationPartner: allFilterState?.integrationPartner == "All Channels" ? null : allFilterState?.integrationPartner,
                            searchTerm: allFilterState?.searchString,
                        }} showTaxColumn={taxInputDetails?.showTaxColumn} />
                    </>
                }

            </div>}

            <ConfirmDialog
                modalStyles={{ maxWidth: '450px' }}
                showButton={false}
                visible={showSpreadsheetWarning && existingSheetTracker}
                toggle={() => {
                    setShowSpreadsheetWarning(!showSpreadsheetWarning);
                }}
                showCloseBtn={false}
                backdrop={continueUpdatingLoading ? "static" : true}
                size={"md"}
            >
                <div className={styles.confirmationContainer}>
                    <div className={`${styles.topContainer} ${styles.warning}`}>
                        <div className={styles.iconContainer}>
                            <Exclamation />
                        </div>
                        <div className={styles.title}>
                            Changes Will Be Lost!
                        </div>

                        <div className={styles.warningText}>
                            {existingSheetTracker && existingSheetTracker?.userId == currentUser?.id ?
                                `Starting a new update will overwrite your previous unsaved changes.` : `Starting a new update will overwrite ${existingSheetTracker?.userName?.toString()?.trim()}’s unsaved changes.`}
                            <br />
                            <br />
                        </div>
                    </div>

                    <div className={styles.bottomContainer}>
                        <MeritoButton disabled={continueUpdatingLoading} size="lg" onClick={() => {
                            setIsBulkUpdateOpen({
                                modalState: true,
                                spreadsheetData: null
                            })
                            setShowSpreadsheetWarning(false);
                        }} isRounded={true} btnType="button" type="secondary-2" btnText="Start New Update" />
                        <MeritoButton size="lg" onClick={continueSheetUpdating} loading={continueUpdatingLoading} showLoadingWithText={true} icon={continueUpdatingLoading ? <RedirectIcon /> : null} disabled={continueUpdatingLoading} isRounded={true} btnType="button" type="primary-1" btnText="Continue Updating" />
                    </div>
                </div>
            </ConfirmDialog>
            <Outlet />


        </>


    );
})

export default ProductCosts;